import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MapPin, Shield, Star, Flame, Zap, Skull, Ghost, Eye, Heart, Clock, Users, ChevronRight, Play } from 'lucide-react';
import { Footer } from '../components/Footer';
import { GameCard } from '../components/GameCard';
import { useEffect, useState, useRef } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

// Animated counter component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Floating ghost animation
const FloatingGhost = ({ delay = 0, className = '' }) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ 
      opacity: [0.1, 0.3, 0.1],
      y: [-20, 20, -20],
      x: [-10, 10, -10]
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    <Ghost className="w-16 h-16 text-red-500/20" />
  </motion.div>
);

// Pulsing eye animation
const PulsingEye = ({ delay = 0, className = '' }) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ 
      scale: [0.8, 1.2, 0.8],
      opacity: [0.2, 0.5, 0.2]
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    <Eye className="w-8 h-8 text-red-600/30" />
  </motion.div>
);

export default function Home() {
  const [games, setGames] = useState([]);
  const [branches, setBranches] = useState([]);
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const gamesSnapshot = await getDocs(collection(db, 'games'));
        const gamesData = gamesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGames(gamesData);

        const branchesSnapshot = await getDocs(collection(db, 'branches'));
        const branchesData = branchesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBranches(branchesData);

        // Fetch active banner (simplified query)
        try {
          const bannersSnapshot = await getDocs(collection(db, 'banners'));
          const activeBanners = bannersSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(banner => banner.isActive !== false) // Get active or undefined
            .sort((a, b) => {
              // Sort by createdAt descending
              const dateA = new Date(a.createdAt || 0);
              const dateB = new Date(b.createdAt || 0);
              return dateB - dateA;
            });
          
          if (activeBanners.length > 0) {
            setBanner(activeBanners[0]);
            console.log('✅ Active banner loaded:', activeBanners[0]);
          } else {
            console.log('ℹ️ No active banners found');
          }
        } catch (bannerError) {
          console.error('❌ Banner fetch error:', bannerError);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const gameLevels = [
    { name: 'Light', age: '12+', color: 'green', icon: Shield, description: 'Yeni başlayanlar üçün' },
    { name: 'Standard', age: '14+', color: 'yellow', icon: Star, description: 'Orta səviyyə qorxu' },
    { name: 'Hard', age: '16+', color: 'orange', icon: Flame, description: 'Təcrübəlilər üçün' },
    { name: 'Ultra Hard', age: '18+', color: 'red', icon: Zap, description: 'Cəsarət tələb edir' }
  ];

  const stats = [
    { label: 'Məmnun Müştəri', value: 50000, suffix: '+', icon: Heart },
    { label: 'Oyun Keçirildi', value: 15000, suffix: '+', icon: Play },
    { label: 'Filial', value: branches.length, suffix: '', icon: MapPin, isDynamic: true },
    { label: 'Unikal Oyun', value: games.length, suffix: '', icon: Skull, isDynamic: true }
  ];

  const features = [
    {
      icon: Skull,
      title: 'Real Qorxu Təcrübəsi',
      description: 'Professional aktyorlar və effektlərlə unudulmaz anlar yaşayın'
    },
    {
      icon: Users,
      title: 'Komanda Oyunu',
      description: 'Dostlarınızla birlikdə qorxunu yaşayın və birlikdə qalib gəlin'
    },
    {
      icon: Clock,
      title: '60 Dəqiqəlik Macəra',
      description: 'Hər oyun müddəti qədər intensiv və əyləncəlidir'
    },
    {
      icon: Shield,
      title: 'Tam Təhlükəsizlik',
      description: 'Professional komanda ilə tam təhlükəsiz mühit'
    }
  ];

  return (
    <div ref={containerRef} className="min-h-screen bg-[#050000] text-white overflow-x-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <FloatingGhost delay={0} className="top-20 left-10" />
        <FloatingGhost delay={2} className="top-40 right-20" />
        <FloatingGhost delay={4} className="bottom-40 left-1/4" />
        <PulsingEye delay={0} className="top-1/3 right-1/4" />
        <PulsingEye delay={1.5} className="bottom-1/3 left-1/3" />
        <PulsingEye delay={3} className="top-2/3 right-10" />
        
        {/* Blood drip effect */}
        <div className="absolute top-0 left-1/4 w-1 h-32 bg-gradient-to-b from-red-600 to-transparent opacity-20" />
        <div className="absolute top-0 right-1/3 w-1 h-48 bg-gradient-to-b from-red-600 to-transparent opacity-15" />
        <div className="absolute top-0 left-2/3 w-1 h-24 bg-gradient-to-b from-red-600 to-transparent opacity-25" />
      </div>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20" data-testid="hero-section">
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-black to-red-900/10"
          style={{ y: backgroundY }}
        />
        
        {/* Animated fog effect */}
        <div className="absolute inset-0 opacity-30">
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-900/50 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          />
        </div>
        
        <div className="text-center z-10 px-4 py-12">
          {banner ? (
            // Firebase Banner
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              {banner.linkUrl ? (
                <Link to={banner.linkUrl}>
                  <div className="max-w-md mx-auto glass-dark rounded-xl overflow-hidden card-hover cursor-pointer border border-red-900/30">
                    <img 
                      src={banner.imageUrl} 
                      alt={banner.title} 
                      className="w-full object-contain" 
                    />
                    <div className="p-6 text-center">
                      <h3 className="text-xl font-bold text-white mb-2">{banner.title}</h3>
                      {banner.subtitle && (
                        <p className="text-gray-400 font-medium">{banner.subtitle}</p>
                      )}
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="max-w-md mx-auto glass-dark rounded-xl overflow-hidden card-hover border border-red-900/30">
                  <img 
                    src={banner.imageUrl} 
                    alt={banner.title} 
                    className="w-full object-contain" 
                  />
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-white mb-2">{banner.title}</h3>
                    {banner.subtitle && (
                      <p className="text-gray-400 font-medium">{banner.subtitle}</p>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            // Default Animated Hero
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.h1 
                  className="text-5xl md:text-7xl font-bold mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.8 }}
                >
                  <span className="text-[#D90429]">Phobia</span>{' '}
                  <span className="text-white">Games</span>
                </motion.h1>
                
                <motion.p 
                  className="text-xl md:text-2xl text-gray-400 mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                >
                  Qorxunun ən yüksək səviyyəsini yaşamağa hazır ol!
                </motion.p>

                <motion.p 
                  className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                >
                  Azərbaycanın ən böyük qorxu evi şəbəkəsi. Real aktyorlar, professional effektlər və unudulmaz təcrübə.
                </motion.p>
                
                <motion.div
                  className="flex flex-col sm:flex-row gap-4 justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                >
                  <Link to="/games">
                    <motion.button 
                      className="btn-primary text-white px-8 py-4 rounded-lg font-bold text-lg flex items-center gap-2 justify-center"
                      whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(220, 38, 38, 0.5)' }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Play className="w-5 h-5" />
                      Oyunlara Bax
                    </motion.button>
                  </Link>
                  <Link to="/branches">
                    <motion.button 
                      className="border-2 border-red-500 text-red-500 hover:bg-red-500/10 px-8 py-4 rounded-lg font-bold text-lg flex items-center gap-2 justify-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <MapPin className="w-5 h-5" />
                      Filiallar
                    </motion.button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          )}
          
          {/* Scroll indicator */}
          <motion.div 
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-gray-500 rounded-full flex justify-center">
              <motion.div 
                className="w-2 h-2 bg-red-500 rounded-full mt-2"
                animate={{ y: [0, 16, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-900/5 to-transparent" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <motion.div
                  className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center"
                  whileHover={{ scale: 1.1, backgroundColor: 'rgba(220, 38, 38, 0.2)' }}
                >
                  <stat.icon className="w-8 h-8 text-red-500" />
                </motion.div>
                <div className="text-4xl font-bold text-white mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-gray-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Phobia Section */}
      <section className="py-20 px-4 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Niyə <span className="text-red-500">Phobia Games</span>?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Azərbaycanın ən keyfiyyətli qorxu evi təcrübəsi
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-gradient-to-br from-gray-900/80 to-black p-6 rounded-xl border border-gray-800 hover:border-red-500/50 transition-all duration-300"
              >
                <motion.div
                  className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-4"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <feature.icon className="w-7 h-7 text-red-500" />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Games Section */}
      {games.length > 0 && (
        <section id="games" className="py-20 px-4" data-testid="games-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Oyunlarımız</h2>
              <p className="text-gray-400">Hər biri unikal qorxu təcrübəsi</p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
              {games.map((game, index) => (
                <GameCard key={game.id} game={game} index={index} />
              ))}
            </div>

            {/* Game Levels */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-gray-900/50 to-black rounded-2xl p-8 border border-gray-800"
            >
              <h3 className="text-2xl font-bold text-white mb-8 text-center">Oyun Səviyyələri</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {gameLevels.map((level, index) => (
                  <motion.div
                    key={level.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    className="text-center p-6 rounded-xl border transition-all duration-300"
                    style={{ 
                      backgroundColor: `rgba(${level.color === 'green' ? '34,197,94' : level.color === 'yellow' ? '234,179,8' : level.color === 'orange' ? '249,115,22' : '220,38,38'}, 0.1)`,
                      borderColor: `rgba(${level.color === 'green' ? '34,197,94' : level.color === 'yellow' ? '234,179,8' : level.color === 'orange' ? '249,115,22' : '220,38,38'}, 0.3)`
                    }}
                  >
                    <level.icon className="w-10 h-10 mx-auto mb-3" style={{ color: level.color === 'green' ? '#22c55e' : level.color === 'yellow' ? '#eab308' : level.color === 'orange' ? '#f97316' : '#ef4444' }} />
                    <h4 className="text-lg font-bold mb-1" style={{ color: level.color === 'green' ? '#4ade80' : level.color === 'yellow' ? '#facc15' : level.color === 'orange' ? '#fb923c' : '#f87171' }}>{level.name}</h4>
                    <p className="text-2xl font-bold mb-1" style={{ color: level.color === 'green' ? '#86efac' : level.color === 'yellow' ? '#fde047' : level.color === 'orange' ? '#fdba74' : '#fca5a5' }}>{level.age}</p>
                    <p className="text-gray-500 text-sm">{level.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Locations Section */}
      {branches.length > 0 && (
        <section id="locations" className="py-20 px-4 relative" data-testid="branches-section">
          <div className="absolute inset-0 bg-gradient-to-b from-red-900/5 via-transparent to-red-900/5" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Filiallarımız</h2>
              <p className="text-gray-400">
                Bakı şəhərində {branches.length} müxtəlif yerdə xidmət göstəririk
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {branches.map((branch, index) => (
                <motion.div
                  key={branch.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gradient-to-br from-gray-900/80 to-black rounded-xl overflow-hidden border border-gray-800 hover:border-red-500/30 transition-all duration-300"
                >
                  {branch.images && branch.images.length > 0 ? (
                    <div className="h-40 overflow-hidden">
                      <motion.img 
                        src={branch.images[0]} 
                        alt={branch.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-red-900/20 to-black flex items-center justify-center">
                      <MapPin className="w-12 h-12 text-red-500/50" />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{branch.name}</h3>
                    <p className="text-gray-400 text-sm mb-4">{branch.address}</p>
                    
                    <div className="flex gap-3">
                      <Link to={`/branches/${branch.id}`} className="flex-1">
                        <motion.button 
                          className="w-full btn-primary text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Ətraflı
                          <ChevronRight className="w-4 h-4" />
                        </motion.button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 via-black to-red-900/20" />
        
        {/* Animated background lines */}
        <div className="absolute inset-0">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent w-full"
              style={{ top: `${20 + i * 15}%` }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 8 + i * 2, repeat: Infinity, ease: "linear" }}
            />
          ))}
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <Ghost className="w-16 h-16 text-red-500 mx-auto mb-6" />
            </motion.div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Qorxuya <span className="text-red-500">Hazırsan</span>?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              İndi rezervasiya et və dostlarınla birlikdə unudulmaz bir gecə keçir!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/games">
                <motion.button 
                  className="btn-primary text-white px-8 py-4 rounded-lg font-bold text-lg"
                  whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(220, 38, 38, 0.5)' }}
                  whileTap={{ scale: 0.95 }}
                >
                  Oyun Seç
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
